import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  Paper,
} from '@mui/material';
import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import { MRT_Localization_EN } from 'material-react-table/locales/en';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { getSelectionLifecycleStatusMeta } from '../utils/status';
import { scheduleMrtFilterPopoverOpen } from '../utils/mrtFilterPopover';

const buildStatusChipLabel = (value) => getSelectionLifecycleStatusMeta(value).label;

const DealerSelectionsMrtTable = ({ rows, onViewHistory, isLoading = false }) => {
  const [columnFilters, setColumnFilters] = useState([]);
  const [sorting, setSorting] = useState([{ id: 'programName', desc: false }]);
  const [columnVisibility, setColumnVisibility] = useState({});
  const [columnOrder, setColumnOrder] = useState([]);
  const [columnPinning, setColumnPinning] = useState({});
  const [pendingFilterColumnId, setPendingFilterColumnId] = useState(null);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuRow, setMenuRow] = useState(null);

  const data = useMemo(() => (Array.isArray(rows) ? rows : []), [rows]);

  const columns = useMemo(
    () => [
      {
        id: 'actions',
        header: 'Actions',
        size: 90,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ row }) => (
          <IconButton
            size="small"
            aria-label={`Open actions for ${row.original.serviceItemName || row.original.referenceId || 'selection'}`}
            onClick={(event) => {
              setMenuAnchorEl(event.currentTarget);
              setMenuRow(row.original);
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
        ),
      },
      { accessorKey: 'programName', header: 'Program', size: 140 },
      { accessorKey: 'vendorName', header: 'Vendor', size: 160 },
      { accessorKey: 'serviceItemName', header: 'Service Item', size: 220 },
      { accessorKey: 'brandsDisplay', header: 'Brands', size: 160 },
      {
        accessorKey: 'productSelectionStatus',
        header: 'Status',
        size: 160,
        Cell: ({ cell }) => buildStatusChipLabel(cell.getValue()),
      },
      { accessorKey: 'selectionProductCode', header: 'Product Code', size: 150 },
      { accessorKey: 'referenceId', header: 'Reference ID', size: 220 },
      { accessorKey: 'dealerPrice', header: 'Dealer Price', size: 120 },
      { accessorKey: 'gmCost', header: 'GM Cost', size: 120 },
      { accessorKey: 'goLiveDateDisplay', header: 'Go Live', size: 150 },
      { accessorKey: 'cancelDateDisplay', header: 'Cancelled', size: 150 },
    ],
    []
  );

  const table = useMaterialReactTable({
    columns,
    data,
    localization: MRT_Localization_EN,
    getRowId: (row) => String(row?.id ?? `${row?.programName}-${row?.referenceId}-${row?.serviceItemName}`),
    enableStickyHeader: true,
    enableColumnResizing: true,
    enableColumnOrdering: true,
    enableColumnPinning: true,
    enableColumnFilters: true,
    enableGlobalFilter: false,
    columnFilterDisplayMode: 'popover',
    initialState: {
      density: 'compact',
      pagination: { pageIndex: 0, pageSize: 25 },
    },
    state: {
      columnFilters,
      sorting,
      columnVisibility,
      columnOrder,
      columnPinning,
      isLoading,
    },
    onColumnFiltersChange: setColumnFilters,
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder,
    onColumnPinningChange: setColumnPinning,
    renderColumnActionsMenuItems: ({ column, closeMenu, internalColumnMenuItems, table: mrtTable }) => {
      const canFilter = column.getCanFilter?.();
      if (!canFilter) return internalColumnMenuItems;

      const addFilterItem = (
        <MenuItem
          key="add-filter"
          onClick={() => {
            setPendingFilterColumnId(column.id);
            closeMenu?.();
          }}
        >
          <FilterAltIcon fontSize="small" sx={{ mr: 1 }} />
          Add Filter
        </MenuItem>
      );

      const internalItems = Array.isArray(internalColumnMenuItems)
        ? [...internalColumnMenuItems]
        : internalColumnMenuItems
          ? [internalColumnMenuItems]
          : [];

      const clearFilterLabel = mrtTable?.options?.localization?.clearFilter || 'Clear Filter';
      const getMenuItemLabel = (node) => {
        if (!node) return '';
        if (typeof node === 'string') return node;
        if (Array.isArray(node)) return node.map(getMenuItemLabel).join('');
        if (typeof node === 'object') {
          if (node.props?.label) return node.props.label;
          if (node.props?.children) return getMenuItemLabel(node.props.children);
        }
        return '';
      };
      const normalizedClearFilterLabel = clearFilterLabel
        .replace(/\s+/g, ' ')
        .trim()
        .toLowerCase();
      const clearFilterIndex = internalItems.findIndex((item) => {
        const label = getMenuItemLabel(item);
        return label.replace(/\s+/g, ' ').trim().toLowerCase() === normalizedClearFilterLabel;
      });

      if (clearFilterIndex === -1) return [addFilterItem, ...internalItems];
      internalItems.splice(clearFilterIndex, 0, addFilterItem);
      return internalItems;
    },
    muiTableHeadCellProps: ({ column }) => {
      const isSorted = Boolean(column.getIsSorted?.());
      return {
        sx: {
          '& .Mui-TableHeadCell-Content button[aria-label*="Filter by"]': {
            opacity: 0,
            pointerEvents: 'none',
          },
          '& .Mui-TableHeadCell-Content-Actions .MuiIconButton-root': {
            display: 'inline-flex',
          },
          '& .MuiTableSortLabel-root': {
            display: isSorted ? 'inline-flex' : 'none',
          },
        },
      };
    },
    muiTableContainerProps: {
      sx: { maxHeight: { xs: 460, md: 620 } },
    },
    renderTopToolbarCustomActions: () => null,
    renderToolbarInternalActions: () => null,
  });

  useEffect(() => {
    if (!pendingFilterColumnId || !table) return undefined;
    return scheduleMrtFilterPopoverOpen({
      table,
      columnId: pendingFilterColumnId,
      buttonSelector: 'button[aria-label*="Filter by"]',
      onComplete: () => setPendingFilterColumnId(null),
    });
  }, [pendingFilterColumnId, table]);

  return (
    <Box sx={{ width: '100%' }}>
      <Paper sx={{ width: '100%' }}>
        <MaterialReactTable table={table} />
      </Paper>
      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={() => {
          setMenuAnchorEl(null);
          setMenuRow(null);
        }}
      >
        <MenuItem
          onClick={() => {
            if (menuRow) onViewHistory?.(menuRow);
            setMenuAnchorEl(null);
            setMenuRow(null);
          }}
        >
          View History
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default DealerSelectionsMrtTable;
